import React, { useState, useEffect, useCallback, useRef } from 'react'
import Header from './components/Header'
import Sidebar from './components/Sidebar'
import ChatWindow from './components/ChatWindow'
import GameStatsHub from './components/GameStatsHub'
import AuthModal from './components/AuthModal'
import { useAuth } from './context/AuthContext'
import { usePulse } from './hooks/useGames'
import './index.css'
import apiClient from './lib/api'

// Mobile breakpoint at 768px (tablet), not 1024px
const getInitialIsMobile = () => (typeof window !== 'undefined' ? window.innerWidth < 768 : false)

export default function App() {
  const { user, isAuthenticated, loading: authLoading, signOut } = useAuth()
  const { data: pulseData } = usePulse()
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [isMobile, setIsMobile] = useState(getInitialIsMobile)
  const [sidebarOpen, setSidebarOpen] = useState(() => !getInitialIsMobile())
  const [rightSidebarOpen, setRightSidebarOpen] = useState(() => !getInitialIsMobile())
  const [rightSidebarWidth, setRightSidebarWidth] = useState(320)
  // Initialize from sessionStorage for non-authenticated users
  const [conversations, setConversations] = useState(() => {
    if (typeof window === 'undefined') return [{ id: 1, title: 'New chat', active: true, backendId: null, messages: [] }]
    try {
      const saved = sessionStorage.getItem('orbglide_conversations')
      if (saved) return JSON.parse(saved)
    } catch (e) { /* ignore */ }
    return [{ id: 1, title: 'New chat', active: true, backendId: null, messages: [] }]
  })
  const [messages, setMessages] = useState(() => {
    if (typeof window === 'undefined') return []
    try {
      const saved = sessionStorage.getItem('orbglide_messages')
      if (saved) return JSON.parse(saved)
    } catch (e) { /* ignore */ }
    return []
  })
  const [activeConversation, setActiveConversation] = useState(() => {
    if (typeof window === 'undefined') return 1
    try {
      const saved = sessionStorage.getItem('orbglide_activeConv')
      if (saved) return parseInt(saved, 10)
    } catch (e) { /* ignore */ }
    return 1
  })
  const [isLoading, setIsLoading] = useState(false)
  const [pendingQuestion, setPendingQuestion] = useState(null)
  const [lastDataFreshness, setLastDataFreshness] = useState(0) // Track last known data freshness
  const saveTimeoutRef = useRef(null)
  const abortControllerRef = useRef(null)
  const lastGameIdRef = useRef(null) // Track last gameId for follow-up context

  // Load conversations when user logs in
  useEffect(() => {
    if (isAuthenticated && user) {
      apiClient.getConversations().then(data => {
        if (data.conversations?.length > 0) {
          setConversations(data.conversations)
          const active = data.conversations.find(c => c.active) || data.conversations[0]
          setActiveConversation(active.id)
          setMessages(active.messages || [])
        }
      }).catch(err => console.error('Failed to load conversations:', err))
    }
  }, [isAuthenticated, user])

  // Save to sessionStorage for non-authenticated users (persists during browser session)
  useEffect(() => {
    if (!isAuthenticated && typeof window !== 'undefined') {
      try {
        sessionStorage.setItem('orbglide_conversations', JSON.stringify(conversations))
        sessionStorage.setItem('orbglide_messages', JSON.stringify(messages))
        sessionStorage.setItem('orbglide_activeConv', String(activeConversation))
      } catch (e) { /* ignore quota errors */ }
    }
  }, [isAuthenticated, conversations, messages, activeConversation])

  // Auto-save conversations when they change (debounced) - for authenticated users
  const saveConversations = useCallback((convs) => {
    if (!isAuthenticated) return
    
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }
    
    saveTimeoutRef.current = setTimeout(() => {
      apiClient.saveConversations(convs).catch(err => 
        console.error('Failed to save conversations:', err)
      )
    }, 1000) // Debounce 1 second
  }, [isAuthenticated])

  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const handleResize = () => {
      const mobile = window.innerWidth < 768
      const wasMobile = isMobile
      setIsMobile(mobile)
      
      // Only auto-close on mobile, keep desktop sidebars open
      if (mobile && !wasMobile) {
        // Switching from desktop to mobile - close sidebars
        setSidebarOpen(false)
        setRightSidebarOpen(false)
      } else if (!mobile && wasMobile) {
        // Switching from mobile to desktop - open sidebars
        setSidebarOpen(true)
        setRightSidebarOpen(true)
      }
    }
    window.addEventListener('resize', handleResize)
    // Don't call handleResize on mount - let initial state handle it
    return () => window.removeEventListener('resize', handleResize)
  }, [isMobile])

  const handleNewChat = () => {
    // Save current messages to the active conversation before switching
    const currentConvs = conversations.map(c => 
      c.id === activeConversation ? { ...c, messages, active: false } : { ...c, active: false }
    )
    
    const newId = Date.now() // Use timestamp for unique ID
    const newConvs = [...currentConvs, { id: newId, title: 'New chat', active: true, backendId: null, messages: [] }]
    setConversations(newConvs)
    setActiveConversation(newId)
    setMessages([])
    saveConversations(newConvs)
  }

  const handleSelectConversation = (id) => {
    // Save current messages before switching
    const updatedConversations = conversations.map(c => ({
      ...c,
      messages: c.id === activeConversation ? messages : c.messages,
      active: c.id === id
    }))
    setConversations(updatedConversations)
    setActiveConversation(id)
    
    // Load messages from selected conversation
    const selected = updatedConversations.find(c => c.id === id)
    setMessages(selected?.messages || [])
    saveConversations(updatedConversations)
  }

  const handleDeleteConversation = (id) => {
    const filtered = conversations.filter(c => c.id !== id)
    setConversations(filtered)
    saveConversations(filtered)
    
    if (activeConversation === id && filtered.length > 0) {
      const next = filtered[0]
      setActiveConversation(next.id)
      setMessages(next.messages || [])
    }
  }

  // Stop/cancel the current AI generation - mark the streaming message as stopped
  const handleStopGeneration = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
    setIsLoading(false)
    setPendingQuestion(null)
    setMessages(prev => {
      const lastStreamingIndex = [...prev].reverse().findIndex(m => m.sender === 'ai' && m.isStreaming)
      if (lastStreamingIndex === -1) return prev
      const index = prev.length - 1 - lastStreamingIndex
      return prev.map((m, i) => {
        if (i !== index) return m
        return {
          ...m,
          text: m.text?.trim() ? m.text : 'Response stopped.',
          isStreaming: false,
          stopped: true,
        }
      })
    })
  }, [])

  const handleSendMessage = async (text, contextOverride = null) => {
    // Cancel any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    abortControllerRef.current = new AbortController()
    
    // Use explicit context from sidebar click, or let backend auto-detect
    // No more "locked" context - each message is independent
    let contextToUse = contextOverride || null
    if (!contextToUse && lastGameIdRef.current) {
      // Follow-up message - hint the last gameId but backend will verify
      contextToUse = { gameId: lastGameIdRef.current }
      console.log('[App] Hinting lastGameId for follow-up:', lastGameIdRef.current)
    }
    const userMessage = { id: Date.now(), text, sender: 'user', context: contextToUse }
    const isFirstMessage = messages.length === 0
    setMessages(prev => [...prev, userMessage])
    setPendingQuestion(text) // Track for thinking animation
    setIsLoading(true)

    const activeConv = conversations.find(c => c.id === activeConversation)
    const conversationId = activeConv?.backendId ?? null
    
    // Include recent conversation history for context (last 6 messages = 3 exchanges)
    const recentHistory = messages
      .filter(m => m.text?.trim() && !m.isStreaming)
      .slice(-6)
      .map(m => ({
        role: m.sender === 'user' ? 'user' : 'assistant',
        content: m.text
      }))

    // Create placeholder AI message for streaming
    const aiMessageId = Date.now() + 1
    let streamedText = ''
    let updatePending = false
    
    // Add empty AI message that will be updated as tokens stream in
    setMessages(prev => [...prev, {
      id: aiMessageId,
      text: '',
      sender: 'ai',
      agent: 'Flash',
      isStreaming: true,
    }])

    // Batched update function - prevents rapid re-renders
    const flushUpdate = () => {
      if (updatePending) return
      updatePending = true
      requestAnimationFrame(() => {
        setMessages(prev => prev.map(m => 
          m.id === aiMessageId 
            ? { ...m, text: streamedText }
            : m
        ))
        updatePending = false
      })
    }

    // Use streaming endpoint for faster perceived response
    await apiClient.sendChatMessageStreaming(
      text,
      conversationId,
      { ...contextToUse, conversationHistory: recentHistory },
      abortControllerRef.current.signal,
      // onToken - batch updates using requestAnimationFrame for smooth rendering
      (token) => {
        streamedText += token
        flushUpdate()
      },
      // onDone - finalize message with freshness data
      (metadata) => {
        setMessages(prev => prev.map(m => 
          m.id === aiMessageId 
            ? { 
                ...m, 
                text: streamedText || 'No response received.',
                agent: metadata.agent || 'Flash',
                questionType: metadata.questionType,
                dataAgeSeconds: metadata.dataAgeSeconds,
                contextQuality: { overall: metadata.contextQuality },
                isStreaming: false,
              }
            : m
        ))
        
        // Update top-level freshness indicator
        if (metadata.dataAgeSeconds > 0) {
          setLastDataFreshness(metadata.dataAgeSeconds)
        }
        
        // Track gameId for follow-up context
        const respondedGameId = contextToUse?.gameId || contextToUse?.id
        if (respondedGameId) {
          lastGameIdRef.current = String(respondedGameId)
          console.log('[App] Updated lastGameId:', lastGameIdRef.current)
        }

        if (metadata.conversation_id && metadata.conversation_id !== activeConv?.backendId) {
          setConversations(prev => prev.map(c =>
            c.id === activeConversation
              ? { ...c, backendId: metadata.conversation_id }
              : c
          ))
        }
        
        setIsLoading(false)
        setPendingQuestion(null)
      },
      // onError - handle errors
      (error) => {
        console.error('Chat streaming error:', error)
        setMessages(prev => prev.map(m => 
          m.id === aiMessageId 
            ? { 
                ...m, 
                text: `Sorry, I encountered an error: ${error.message}. Please try again.`,
                agent: 'Flash',
                isStreaming: false,
              }
            : m
        ))
        setIsLoading(false)
        setPendingQuestion(null)
      }
    )

    if (isFirstMessage) {
      setConversations(prev => {
        const updated = prev.map(c =>
          c.id === activeConversation
            ? { ...c, title: text.substring(0, 30) + (text.length > 30 ? '...' : '') }
            : c
        )
        saveConversations(updated)
        return updated
      })
    }
    
    // Save messages to conversation after each exchange
    setConversations(prev => {
      const updated = prev.map(c =>
        c.id === activeConversation
          ? { ...c, messages: [...messages, userMessage] }
          : c
      )
      saveConversations(updated)
      return updated
    })
  }

  const handleResizeStart = (e) => {
    e.preventDefault()
    const startX = e.clientX
    const startWidth = rightSidebarWidth

    const handleMouseMove = (moveEvent) => {
      const diff = startX - moveEvent.clientX
      const newWidth = Math.max(250, Math.min(600, startWidth + diff))
      setRightSidebarWidth(newWidth)
    }

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  return (
    <div className="flex flex-col h-full min-h-screen bg-white">
      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)} 
      />
      
      {/* Persistent Header */}
      <Header 
        onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
        onToggleRightSidebar={() => setRightSidebarOpen(!rightSidebarOpen)}
        sidebarOpen={sidebarOpen}
        rightSidebarOpen={rightSidebarOpen}
        user={user}
        onSignIn={() => setShowAuthModal(true)}
        isPro={false}
        hasLiveGame={false}
      />
      
      {/* Left Sidebar - Fixed overlay */}
      <Sidebar 
        open={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        conversations={conversations}
        activeConversation={activeConversation}
        onNewChat={handleNewChat}
        user={user}
        onSignIn={() => setShowAuthModal(true)}
        onSignOut={signOut}
        onSelectConversation={handleSelectConversation}
        onDeleteConversation={handleDeleteConversation}
        isMobile={isMobile}
      />
      
      {/* Main content area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Chat - Primary Surface */}
        <div className="flex-1 flex flex-col relative overflow-hidden">
          <ChatWindow 
            messages={messages}
            onSendMessage={handleSendMessage}
            onStopGeneration={handleStopGeneration}
            onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
            onToggleRightSidebar={() => setRightSidebarOpen(!rightSidebarOpen)}
            isLoading={isLoading}
            sidebarOpen={sidebarOpen}
            rightSidebarOpen={rightSidebarOpen}
            pendingQuestion={pendingQuestion}
            dataFreshness={lastDataFreshness}
          />
        </div>
        
        {/* Right Sidebar - Collapsible Games Panel */}
      {rightSidebarOpen && (
        <>
          {/* Mobile backdrop - tap to close */}
          {isMobile && (
            <div 
              className="fixed inset-0 bg-black/30 z-40"
              onClick={() => setRightSidebarOpen(false)}
            />
          )}
          <div 
            className={`
              ${isMobile ? 'fixed right-0 top-0 bottom-0' : 'relative shrink-0'} bg-white border-l 
              transition-transform duration-300 z-50
              ${rightSidebarOpen ? 'translate-x-0' : 'translate-x-full'}
            `} 
            style={{ 
              borderColor: 'var(--border)',
              width: isMobile ? '85%' : `${rightSidebarWidth}px`,
              maxWidth: isMobile ? '400px' : '500px',
              minWidth: isMobile ? '280px' : '280px',
              paddingTop: isMobile ? 'env(safe-area-inset-top)' : 0,
              paddingBottom: isMobile ? 'env(safe-area-inset-bottom)' : 0,
              borderTopLeftRadius: isMobile ? '20px' : '0',
              borderBottomLeftRadius: isMobile ? '20px' : '0',
              boxShadow: isMobile ? '-4px 0 24px rgba(0,0,0,0.1)' : '0 0 12px rgba(0,0,0,0.05)'
            }}
          >
            {/* Resize handle - desktop only */}
            {!isMobile && (
              <div
                onMouseDown={handleResizeStart}
                className="absolute left-0 top-0 w-1.5 h-full cursor-col-resize hover:bg-pink-400 transition-colors z-10"
                style={{ backgroundColor: 'transparent' }}
                title="Drag to resize"
              />
            )}
          <GameStatsHub 
            open={rightSidebarOpen}
            onSidebarQuestion={handleSendMessage}
            isLoading={isLoading}
            onClose={() => setRightSidebarOpen(false)}
          />
          </div>
        </>
      )}
      </div>
    </div>
  )
}
